import { Drawer, Tabs } from "@chakra-ui/react"
import { AugmentationPanel } from "@pathwright/ux/src/components/augmentation/AugmentationLayout"
import {
  AugmentationToolbarOption,
  useAugmentationToolbar
} from "@pathwright/ux/src/components/augmentation/AugmentationToolbar"
import useAugmentationPanel from "@pathwright/ux/src/components/augmentation/useAugmentationPanel"
import React from "react"
import { useToggleIntercomUI } from "../../intercom/IntercomProvider"
import { useCertificateContext } from "../context/CertificateContext"
import { useCertificateState } from "../context/CertificateState"
import CertificateAugmentationHeader from "./CertificateAugmentationHeader"
import CertificateSubmitButton from "./CertificateSubmitButton"
import DesignPanel from "./DesignPanel"
import VariablesPanel from "./VariablesPanel"

export function useCertificateAugmentationToolbar() {
  const options: AugmentationToolbarOption[] = [
    {
      key: "design",
      icon: "edit",
      label: "Design",
      "aria-label": "Certificate design settings",
      AugmentationComponent: DesignPanel
    },
    {
      key: "variables",
      icon: "test-tube",
      label: "Variables",
      "aria-label": "Certificate variables settings",
      AugmentationComponent: VariablesPanel
    }
  ]

  return useAugmentationToolbar(options)
}

const CertificateAugmentation: AugmentationPanel = React.forwardRef(
  ({ toolbar, disclosure, gutter, offset, allowOverlay }, ref) => {
    // Hide Intercom UI when drawer is open.
    useToggleIntercomUI(disclosure?.open)

    const { getDrawerProps, getDrawerPositionerProps, getDrawerContentProps } =
      useAugmentationPanel({
        disclosure,
        offset: { ...offset, top: 50 },
        allowOverlay,
        gutter,
        size: "xs"
      })

    const { certificate } = useCertificateContext()
    const {
      certificateStoredState: { discardCount }
    } = useCertificateState()
    const showSubmit = Boolean(certificate || discardCount)

    return (
      <Tabs.Root
        variant="line"
        colorPalette="gray"
        value={toolbar.selectedKey}
        lazyMount
        unmountOnExit={false}
        size="lg"
      >
        <Drawer.Root {...getDrawerProps()}>
          <Drawer.Positioner {...getDrawerPositionerProps()}>
            <Drawer.Content
              ref={ref as React.Ref<HTMLDivElement>}
              {...getDrawerContentProps()}
            >
              <Drawer.Header p={0}>
                <CertificateAugmentationHeader
                  toolbar={toolbar}
                  disclosure={disclosure}
                />
              </Drawer.Header>
              <Drawer.Body p={0}>
                {toolbar.options.map((option) =>
                  option.AugmentationComponent ? (
                    <Tabs.Content key={option.key} value={option.key} p={0}>
                      <option.AugmentationComponent toolbar={toolbar} />
                    </Tabs.Content>
                  ) : null
                )}
              </Drawer.Body>
              {showSubmit && (
                <Drawer.Footer justifyContent="center">
                  <CertificateSubmitButton />
                </Drawer.Footer>
              )}
            </Drawer.Content>
          </Drawer.Positioner>
        </Drawer.Root>
      </Tabs.Root>
    )
  }
)

export default CertificateAugmentation
